<template>
  <div>
    <div ref="graphContainer" class="w-full h-full">
      <div :id="`container-${chart.Id}`" class="svg-container"></div>
    </div>
  </div>
</template>

<script setup>
import { sankey, sankeyLinkHorizontal } from "d3-sankey";
import { ref } from "vue";
// https://stackblitz.com/edit/vue-g6khps?file=src%2Fcomponents%2FUI.vue
import * as d3 from "d3";

// #region Props, Emits
const props = defineProps({
  chart: {
    type: Object,
    required: true,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const graphContainer = ref(null);
// #endregion

// #region Functions
function loadGraph(id, width, height) {
  const sankeyData = props.chart.propiedades.sankey;
  const sankeyNodes = [];

  sankeyData.forEach((data, index) => {
    sankeyNodes.push({
      node: index,
      name: data.nombre,
      id: data.nombre,
      color: data.color,
    });
  });

  const items = {
    nodes: sankeyNodes,
    links: [
      { source: sankeyNodes[0].name, target: sankeyNodes[1].name, value: 1 },
      { source: sankeyNodes[0].name, target: sankeyNodes[3].name, value: 1 },
      { source: sankeyNodes[2].name, target: sankeyNodes[3].name, value: 2 },
    ],
  };

  /*
  const items = {
    nodes: [
      { node: 0, name: "node0", id: "IdNode0", color: "#e65757" },
      { node: 1, name: "node1", id: "IdNode1", color: "#ffa500" },
      { node: 2, name: "node2", id: "IdNode2", color: "#004172" },
      { node: 3, name: "node3", id: "IdNode3", color: "#64cd8a" },
    ],
    links: [
      { source: "node0", target: "node1", value: 1 },
      { source: "node0", target: "node3", value: 1 },
      { source: "node2", target: "node3", value: 4 },
    ],
  };
  */

  // graphContainer width and height
  const nodeWidth = 100;
  const nodePadding = 20; // Separación entre nodos
  const margin = 40;

  width = width - margin;
  height = height - margin;

  // Create the container.
  const container = d3.select(id);

  // Create the SVG element.
  const svg = container
    .append("svg")
    .attr("width", width)
    .attr("height", height);

  const ENABLE_LINKS_GRADIENTS = true;

  const { nodes, links } = sankey()
    .nodeId((d) => d.name)
    .nodeWidth(nodeWidth)
    .nodePadding(nodePadding)
    .extent([
      [0, 0],
      [width, height],
    ])(items);

  const link = svg
    .append("g")
    .attr("fill", "none")
    .attr("stroke-opacity", 0.8)
    .selectAll("g")
    .data(links)
    .join("g");
  // .style("mix-blend-mode", "multiply");

  let lowerWidth = 0;
  let lowerValue = 0;

  // get links height
  links.reduce((acc, link) => {
    if (link.index == 0) {
      lowerWidth = link.width;
      lowerValue = link.value;
    }

    if (link.width < lowerWidth) {
      lowerWidth = link.width;
    }

    if (link.value < lowerValue) {
      lowerValue = link.value;
    }
  }, 0);

  svg
    .append("g")
    .attr("stroke", "#000")
    .attr("stroke-width", "0")
    .selectAll("rect")
    .data(nodes)
    .join("rect")
    .attr("x", (d) => d.x0)
    .attr("y", (d) => d.y0)
    .attr("height", (d) => {
      let height = 0;
      let totalValue = 0;

      if (d.sourceLinks.length) {
        d.sourceLinks.forEach((link) => {
          totalValue += link.value;
        });
      } else {
        d.targetLinks.forEach((link) => {
          totalValue += link.value;
        });
      }

      height = (totalValue / lowerValue) * lowerWidth;

      return height;
    })
    .attr("width", (d) => d.x1 - d.x0)
    .attr("fill", (d) => d.color)
    .append("title")
    .text((d) => `${d.name}\n${d.value}`);

  if (ENABLE_LINKS_GRADIENTS) {
    const gradient = link
      .append("linearGradient")
      .attr("id", (d) => (d.uid = `${d.source.id}-to-${d.target.id}`))
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", (d) => d.source.x1)
      .attr("x2", (d) => d.target.x0);

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", (d) => d.source.color);

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", (d) => d.target.color);
  }

  link
    .append("path")
    .attr("d", sankeyLinkHorizontal())
    .attr("stroke", (d) =>
      !ENABLE_LINKS_GRADIENTS ? d.color : `url(#${d.uid})`
    )
    .attr("stroke-width", (d) => Math.max(1, d.width));

  svg
    .append("g")
    .attr("font-family", "sans-serif")
    .attr("font-size", 15)
    .attr("text-anchor", "middle")
    .attr("fill", "white")
    .selectAll("text")
    .data(nodes)
    .join("text")
    .attr("x", (d) => (d.x1 + d.x0) / 2)
    .attr("y", (d) => d.y0 + 15)
    .text((d) => d.name);
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
defineExpose({
  graphContainer,
  loadGraph,
});
// #endregion
</script>

<style lang="scss" scoped>
.svg-container {
  position: relative;
  width: 100%;
  height: 100%;

  :deep(svg) {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
